/**
 * MarketingEventLog — /log/marketing/event/:id
 * Per-event result entry. Marketing logs what actually happened.
 * Mi6 palette: black #0B0B0F · red #DC2626.
 */
import { useState, useEffect } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { useParams, useLocation } from "wouter";
import {
  loadEvents,
  saveEvents,
  seedEventsIfEmpty,
  type MarketingEvent,
} from "@/lib/marketing-hook-generator";

const MI6_BLACK = "#0B0B0F";
const MI6_RED  = "#DC2626";

function NumField({
  label,
  value,
  onChange,
  prefix,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  prefix?: string;
}) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">{label}</label>
      <div className="relative flex items-center">
        {prefix && <span className="absolute left-3 text-neutral-500 text-sm pointer-events-none">{prefix}</span>}
        <input
          type="number"
          min={0}
          value={value || ""}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="w-full border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
          style={{ paddingLeft: prefix ? "28px" : "12px", paddingRight: "12px", paddingTop: "8px", paddingBottom: "8px" }}
          placeholder="0"
        />
      </div>
    </div>
  );
}

export default function MarketingEventLog() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const eventId = params.id;

  const [events, setEvents] = useState<MarketingEvent[]>([]);
  const [event, setEvent] = useState<MarketingEvent | null>(null);
  const [saved, setSaved] = useState(false);

  // Actual fields
  const [costActual, setCostActual] = useState(0);
  const [totalAttendees, setTotalAttendees] = useState(0);
  const [registeredWithUs, setRegisteredWithUs] = useState(0);
  const [boothStops, setBoothStops] = useState(0);
  const [mqlsAdded, setMqlsAdded] = useState(0);
  const [suspectsQualified, setSuspectsQualified] = useState(0);
  const [prospectsQualified, setProspectsQualified] = useState(0);
  const [immediateOpportunities, setImmediateOpportunities] = useState(0);
  const [followUpMeetingsBooked, setFollowUpMeetingsBooked] = useState(0);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    const evs = seedEventsIfEmpty();
    setEvents(evs);
    const ev = evs.find((e) => e.id === eventId);
    if (ev) {
      setEvent(ev);
      if (ev.actual) {
        setCostActual(ev.actual.costActual || 0);
        setTotalAttendees(ev.actual.totalAttendees || 0);
        setRegisteredWithUs(ev.actual.registeredWithUs || 0);
        setBoothStops(ev.actual.boothStops || 0);
        setMqlsAdded(ev.actual.mqlsAdded || 0);
        setSuspectsQualified(ev.actual.suspectsQualified || 0);
        setProspectsQualified(ev.actual.prospectsQualified || 0);
        setImmediateOpportunities(ev.actual.immediateOpportunities || 0);
        setFollowUpMeetingsBooked(ev.actual.followUpMeetingsBooked || 0);
        setNotes(ev.actual.notes || "");
      }
    }
  }, [eventId]);

  function handleSave(markComplete = false) {
    if (!event) return;
    const updated: MarketingEvent = {
      ...event,
      status: markComplete ? "Completed" : event.status,
      actual: {
        costActual,
        totalAttendees,
        registeredWithUs,
        boothStops,
        mqlsAdded,
        suspectsQualified,
        prospectsQualified,
        immediateOpportunities,
        followUpMeetingsBooked,
        notes,
      },
    };
    const newEvents = events.map((e) => e.id === event.id ? updated : e);
    saveEvents(newEvents);
    setEvents(newEvents);
    setEvent(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex flex-col">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="text-4xl font-bold text-neutral-200 mb-2">404</div>
            <div className="text-neutral-500 mb-4">Event not found (ID: {eventId})</div>
            <a href="/marketing/calendar" className="text-sm font-semibold" style={{ color: MI6_RED }}>← Back to Calendar</a>
          </div>
        </div>
      </div>
    );
  }

  const budgetVariance = event.cost && costActual ? costActual - event.cost : null;

  return (
    <OperatorShell breadcrumbs={[{ label: "Marketing Event Log" }]} pageTitle="Marketing Event Log" pageMeta="Per-event results">

      {/* Header */}
      <div className="sticky top-14 z-30 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <a href="/marketing/calendar" className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-600 transition-colors">← Calendar</a>
              <span className="text-neutral-200">·</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500">Event Result Log</span>
            </div>
            <h1 className="text-lg font-bold text-neutral-900 truncate max-w-xl">{event.name}</h1>
          </div>
          <div className="flex items-center gap-3">
            {saved && (
              <span className="text-[11px] text-emerald-600 font-semibold bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                ✓ Saved
              </span>
            )}
            <button
              onClick={() => handleSave(false)}
              className="px-4 py-2 rounded-lg text-sm border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors"
            >
              Save Draft
            </button>
            <button
              onClick={() => handleSave(true)}
              className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: MI6_RED }}
            >
              Mark Complete
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex-1 w-full">

        {/* Event meta */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 mb-5" style={{ borderLeft: `4px solid ${MI6_BLACK}` }}>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Activity Type</div>
              <div className="font-medium text-neutral-700">{event.activityType}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Date(s)</div>
              <div className="font-medium text-neutral-700">
                {new Date(event.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                {event.endDate ? `–${new Date(event.endDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : `, ${new Date(event.startDate).getFullYear()}`}
              </div>
            </div>
            {event.location && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Location</div>
                <div className="font-medium text-neutral-700">{event.location}</div>
              </div>
            )}
            {event.cost && (
              <div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-neutral-500 mb-0.5">Budgeted Cost</div>
                <div className="font-medium text-neutral-700">${event.cost.toLocaleString()}</div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

          {/* Cost */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Cost Actual</div>
            <NumField label="Actual Cost ($)" value={costActual} onChange={setCostActual} prefix="$" />
            {budgetVariance !== null && (
              <div className={`mt-2 text-xs font-semibold ${budgetVariance > 0 ? "text-red-600" : "text-emerald-600"}`}>
                {budgetVariance > 0 ? "↑" : "↓"} ${Math.abs(budgetVariance).toLocaleString()} {budgetVariance > 0 ? "over" : "under"} budget
              </div>
            )}
          </div>

          {/* Attendance */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Attendance</div>
            <div className="space-y-3">
              <NumField label="Total Attendees" value={totalAttendees} onChange={setTotalAttendees} />
              <NumField label="Registered With Us" value={registeredWithUs} onChange={setRegisteredWithUs} />
              <NumField label="Booth Stops" value={boothStops} onChange={setBoothStops} />
            </div>
          </div>

          {/* Conversion */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Conversion</div>
            <div className="space-y-3">
              <NumField label="MQLs Added" value={mqlsAdded} onChange={setMqlsAdded} />
              <NumField label="Suspects Qualified" value={suspectsQualified} onChange={setSuspectsQualified} />
              <NumField label="Prospects Qualified" value={prospectsQualified} onChange={setProspectsQualified} />
              <NumField label="Immediate Opportunities" value={immediateOpportunities} onChange={setImmediateOpportunities} />
              <NumField label="Follow-up Meetings Booked" value={followUpMeetingsBooked} onChange={setFollowUpMeetingsBooked} />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Notes</div>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#0B0B0F] resize-none"
              rows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Key takeaways, follow-up actions, what worked, what didn't..."
            />
          </div>
        </div>

        {/* Summary KPIs if there's existing actual data */}
        {(mqlsAdded > 0 || followUpMeetingsBooked > 0) && (
          <div className="mt-5 bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neutral-500 font-bold mb-4">Result Summary</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              {[
                { label: "MQLs Added", value: mqlsAdded },
                { label: "Suspects", value: suspectsQualified },
                { label: "Prospects", value: prospectsQualified },
                { label: "Opps", value: immediateOpportunities },
                { label: "Meetings", value: followUpMeetingsBooked },
              ].map((kpi) => (
                <div key={kpi.label} className="text-center">
                  <div className="text-2xl font-bold tabular-nums" style={{ color: MI6_BLACK }}>{kpi.value || "—"}</div>
                  <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 mt-0.5">{kpi.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </OperatorShell>
  );
}
